import { CommonModule } from '@angular/common';
import { compararValoresOrdenacao } from '../../../../../shared/helpers/ordenacao-tabela.helper';
import { Component, EventEmitter, Input, Output } from '@angular/core';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatTableModule } from '@angular/material/table';
import { MatSortModule, Sort } from '@angular/material/sort';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

import { AlertService, AlertType } from '../../../../../shared/services/alert.service';
import { ListaPrecoService } from '../../../../../shared/services/apis/produto/lista-preco.service';
import { ProdutoListaPrecoStruct } from '../../../../../shared/services/structs/produto/produto-lista-preco.struct';
import { GetPrecosProdutosAutomaticosRequest } from '../../../../../shared/services/requests/produto/get-precos-produtos-automaticos.request';
import { SelectPesquisavelComponent } from '../../../../../shared/components/select-pesquisavel/select-pesquisavel.component';
import { environment } from '../../../../../../environments/environment';
import { NgxCurrencyDirective } from 'ngx-currency';
import { MaskService } from '../../../../../shared/services/mask.service';
import { AlertCardComponent } from '../../../../../shared/components/alert-card/alert-card.component';
import { AbntPipe } from '../../../../../shared/pipes/abnt.pipe';

@Component({
  selector: 'app-lista-preco-cadastro-produtos',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatButtonModule,
    MatIconModule,
    MatTableModule,
    MatSortModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
    SelectPesquisavelComponent,
    NgxCurrencyDirective,
    AlertCardComponent,
    AbntPipe
  ],
  templateUrl: './lista-preco-cadastro-produtos.component.html',
  styleUrl: './lista-preco-cadastro-produtos.component.css'
})
export class ListaPrecoCadastroProdutosComponent {

  @Input() produtos: ProdutoListaPrecoStruct[] = [];
  @Input() isSelecaoAutomatica: boolean = false;
  @Input() isMetodoManual: boolean = false;
  @Input() isPadrao: boolean = false;
  @Input() configAjuste: { idAcaoAjustePreco?: number; idUnidadeAjustePreco?: number; idTipoArredondamento?: number; valorAjuste?: number } = {};
  @Input() isEditMode: boolean = false;

  @Output() produtosChange = new EventEmitter<ProdutoListaPrecoStruct[]>();

  public currencyMask = this.maskService.getDynamicCurrencyMask();

  public urlProduto = `${environment.urlApiProduto}Lookup/listProdutoSelect`;
  public displayedColumns: string[] = ['nome', 'codigoSku', 'precoOriginal', 'precoLista', 'margem', 'acoes'];

  public showAddRow: boolean = false;
  public isCalculando: boolean = false;
  public novoProdutoControl = new FormControl(null);
  public novoPrecoLista: number | null = null;
  public produtoSelecionado: any = null;

  // Filtro
  public filtroTexto: string = '';

  // Ordenação
  public sortColumn: string = '';
  public sortDirection: 'asc' | 'desc' | '' = '';

  // Paginação "Ver mais"
  private readonly PAGE_SIZE = 30;
  public displayLimit: number = this.PAGE_SIZE;

  get produtosFiltrados(): ProdutoListaPrecoStruct[] {
    let lista = this.produtos;

    // Filtro por texto
    if (this.filtroTexto) {
      const termo = this.filtroTexto.toLowerCase();
      lista = lista.filter(p =>
        (p.nome?.toLowerCase().includes(termo)) ||
        (p.codigoSku?.toLowerCase().includes(termo)) ||
        (p.precoOriginal?.toString().includes(termo)) ||
        (p.precoLista?.toString().includes(termo)) ||
        (p.margem?.toFixed(2).includes(termo))
      );
    }

    // Ordenação
    if (this.sortColumn && this.sortDirection) {
      const dir = this.sortDirection === 'asc' ? 1 : -1;
      lista = [...lista].sort((a: any, b: any) =>
        compararValoresOrdenacao(a[this.sortColumn] ?? '', b[this.sortColumn] ?? '') * dir
      );
    }

    return lista;
  }

  get produtosVisiveis(): ProdutoListaPrecoStruct[] {
    return this.produtosFiltrados.slice(0, this.displayLimit);
  }

  get hasMore(): boolean {
    return this.displayLimit < this.produtosFiltrados.length;
  }

  verMais(): void {
    this.displayLimit += this.PAGE_SIZE;
  }

  onSort(sort: Sort): void {
    this.sortColumn = sort.active;
    this.sortDirection = sort.direction;
    this.displayLimit = this.PAGE_SIZE;
  }

  onFilter(): void {
    this.displayLimit = this.PAGE_SIZE;
  }

  // Produto em modo de edição inline
  public editingIndex: number | null = null;
  public editingPrecoLista: number | null = null;

  constructor(
    private listaPrecoService: ListaPrecoService,
    private alertService: AlertService,
    private maskService: MaskService,
  ) {
  }

  get isDisabled(): boolean {
    return this.isSelecaoAutomatica || this.isPadrao;
  }

  adicionarLinha(): void {
    this.showAddRow = true;
    this.novoProdutoControl.setValue(null);
    this.novoPrecoLista = null;
    this.produtoSelecionado = null;
  }

  onNovoProdutoSelecionado(item: any): void {
    this.produtoSelecionado = item;
  }

  confirmarAdicao(): void {
    if (!this.produtoSelecionado) {
      this.alertService.show('Atenção', 'Selecione um produto.', AlertType.warning);
      return;
    }

    if (this.isMetodoManual) {
      // Modo manual: valida preço lista
      if (!this.novoPrecoLista || this.novoPrecoLista <= 0) {
        this.alertService.show('Atenção', 'Informe um Preço na Lista maior que zero.', AlertType.warning);
        return;
      }
      const novo: ProdutoListaPrecoStruct = {
        idProduto: this.produtoSelecionado.idProduto,
        nome: this.produtoSelecionado.descricao,
        codigoSku: this.produtoSelecionado.codigoSku,
        precoOriginal: this.produtoSelecionado.preco ?? 0,
        precoLista: this.novoPrecoLista,
        margem: this.produtoSelecionado.custoMedio > 0
          ? ((this.novoPrecoLista - this.produtoSelecionado.custoMedio) / this.produtoSelecionado.custoMedio) * 100
          : 0,
        custoMedio: this.produtoSelecionado.custoMedio,
      };
      this.emitirNovaProdutoLista([...this.produtos, novo]);
      this.cancelarAdicao();
    } else {
      // Modo automático: calcular via API
      if (!this.configAjuste.idAcaoAjustePreco || !this.configAjuste.idUnidadeAjustePreco) {
        this.alertService.show('Atenção', 'Configure a Ação, Unidade e Valor de Ajuste antes de adicionar produtos.', AlertType.warning);
        return;
      }

      this.isCalculando = true;

      const todosIds = [...this.produtos.map(p => p.idProduto), this.produtoSelecionado.idProduto];

      const request = new GetPrecosProdutosAutomaticosRequest();
      request.listIdsProdutos = todosIds;
      request.idAcaoAjustePreco = this.configAjuste.idAcaoAjustePreco!;
      request.idUnidadeAjustePreco = this.configAjuste.idUnidadeAjustePreco!;
      request.idTipoArredondamento = this.configAjuste.idTipoArredondamento ?? 0;
      request.valorAjuste = this.configAjuste.valorAjuste ?? 0;

      this.listaPrecoService.CalcularPrecosProdutosAutomaticos(request).subscribe({
        next: (res) => {
          this.isCalculando = false;
          if (res.isError) {
            this.alertService.show('Erro', res.errorDescription, AlertType.error);
            return;
          }
          if (res.produtos) {
            this.emitirNovaProdutoLista(res.produtos);
          }
          this.cancelarAdicao();
        },
        error: () => {
          this.isCalculando = false;
          this.alertService.show('Erro', 'Erro ao calcular preços.', AlertType.error);
        }
      });
    }
  }

  cancelarAdicao(): void {
    this.showAddRow = false;
    this.novoProdutoControl.setValue(null);
    this.novoPrecoLista = null;
    this.produtoSelecionado = null;
  }

  removerProduto(index: number): void {
    const nova = this.produtos.filter((_, i) => i !== index);
    this.emitirNovaProdutoLista(nova);
  }

  editarProduto(index: number): void {
    // Edição manual do preço só é permitida quando o método de ajuste é Manual.
    // No modo automático o preço é calculado pela API conforme ação/unidade/valor de ajuste.
    if (!this.isMetodoManual) return;
    this.editingIndex = index;
    this.editingPrecoLista = this.produtos[index].precoLista ?? null;
  }

  confirmarEdicao(): void {
    if (this.editingIndex === null) return;
    if (this.isMetodoManual && (!this.editingPrecoLista || this.editingPrecoLista <= 0)) {
      this.alertService.show('Atenção', 'Informe um Preço na Lista maior que zero.', AlertType.warning);
      return;
    }
    const nova = [...this.produtos];
    nova[this.editingIndex] = { ...nova[this.editingIndex], precoLista: this.editingPrecoLista };
    this.emitirNovaProdutoLista(nova);
    this.editingIndex = null;
    this.editingPrecoLista = null;
  }

  cancelarEdicao(): void {
    this.editingIndex = null;
    this.editingPrecoLista = null;
  }

  private emitirNovaProdutoLista(lista: ProdutoListaPrecoStruct[]): void {
    this.produtosChange.emit(lista);
  }
}
